import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, Clock, Instagram, Facebook, Send, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const subjects = [
    { id: 'general', label: 'General Inquiry' },
    { id: 'order', label: 'Order Question' },
    { id: 'custom', label: 'Custom Order Request' },
    { id: 'returns', label: 'Returns & Exchanges' },
    { id: 'wholesale', label: 'Wholesale Inquiry' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      logger.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="contact-success">
      <Seo title="Contact" description={"Send Lindy a message — about a custom order, a market visit, sizing, anything. We reply within 24 hours."} path="/contact" />
        <div className="container-custom px-6 md:px-12 lg:px-24 max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 bg-[#52796F] rounded-full flex items-center justify-center mx-auto mb-8">
            <Send className="w-10 h-10 text-[#FAF9F6]" strokeWidth={1.5} />
          </div>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-4">
            Message Sent!
          </h1>
          <p className="text-[#52796F] mb-8">
            Thank you for reaching out. We'll get back to you within 24-48 hours.
          </p>
          <Link to="/" className="btn-primary inline-block">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16" data-testid="contact-page">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Get in Touch</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Contact Us
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Have a question or just want to say hello? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#354F52] rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-lg text-[#354F52] mb-1">Email Us</h3>
                <a 
                  href="mailto:hello@whisperingwristwear.com" 
                  className="text-[#52796F] hover:text-[#D4AF37] transition-colors"
                >
                  hello@whisperingwristwear.com
                </a>
                <p className="text-sm text-[#84A98C] mt-1">For general inquiries</p>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#354F52] rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-lg text-[#354F52] mb-1">Response Time</h3>
                <p className="text-[#52796F]">Within 24-48 hours</p>
                <p className="text-sm text-[#84A98C] mt-1">Monday - Friday</p>
              </div>
            </div>

            {/* Location */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#354F52] rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-lg text-[#354F52] mb-1">Studio</h3>
                <p className="text-[#52796F]">Online · Markets around Paphos, Cyprus</p>
                <p className="text-sm text-[#84A98C] mt-1">Cyprus</p>
              </div>
            </div>

            {/* Social */}
            <div className="pt-6 border-t border-[#CAD2C5]/50">
              <h3 className="font-serif text-lg text-[#354F52] mb-4">Follow Us</h3>
              <div className="flex items-center gap-3">
                <a 
                  href="#" 
                  className="w-10 h-10 bg-[#F5F5F0] rounded-full flex items-center justify-center hover:bg-[#354F52] hover:text-[#FAF9F6] transition-colors text-[#354F52]"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5" strokeWidth={1.5} />
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 bg-[#F5F5F0] rounded-full flex items-center justify-center hover:bg-[#354F52] hover:text-[#FAF9F6] transition-colors text-[#354F52]"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <Facebook className="w-5 h-5" strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-[#F5F5F0] p-8 md:p-10 rounded-sm">
              <h2 className="font-serif text-2xl text-[#354F52] mb-6">Send a Message</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#354F52]">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jane Smith"
                    className="border-[#CAD2C5] focus:border-[#354F52] bg-white"
                    required
                    data-testid="contact-name"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#354F52]">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jane@example.com"
                    className="border-[#CAD2C5] focus:border-[#354F52] bg-white"
                    required
                    data-testid="contact-email"
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2 mb-6">
                <Label htmlFor="subject" className="text-[#354F52]">
                  Subject
                </Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) => setFormData({ ...formData, subject: value })}
                >
                  <SelectTrigger className="border-[#CAD2C5] focus:border-[#354F52] bg-white" data-testid="contact-subject">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2 mb-8">
                <Label htmlFor="message" className="text-[#354F52]">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="How can we help you?"
                  className="border-[#CAD2C5] focus:border-[#354F52] bg-white min-h-[150px]"
                  required
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="contact-submit"
              >
                {loading ? 'Sending...' : (
                  <>
                    <Send className="w-4 h-4" strokeWidth={1.5} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl text-[#354F52] text-center mb-10">
            Frequently Asked Questions
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#F5F5F0] p-6 rounded-sm">
              <h3 className="font-serif text-lg text-[#354F52] mb-2">
                How long does shipping take?
              </h3>
              <p className="text-[#52796F] text-sm leading-relaxed">
                We offer free shipping across Cyprus via ACS Couriers. Standard orders 
                ship within 1-2 days, delivery takes 2-4 business days. Custom orders may take 
                3-5 business days as each piece is handcrafted to order.
              </p>
            </div>

            <div className="bg-[#F5F5F0] p-6 rounded-sm">
              <h3 className="font-serif text-lg text-[#354F52] mb-2">
                Can I modify my order after placing it?
              </h3>
              <p className="text-[#52796F] text-sm leading-relaxed">
                Contact us within 24 hours of placing your order and we'll do our best 
                to accommodate changes before production begins.
              </p>
            </div>

            <div className="bg-[#F5F5F0] p-6 rounded-sm">
              <h3 className="font-serif text-lg text-[#354F52] mb-2">
                Do you offer gift wrapping?
              </h3>
              <p className="text-[#52796F] text-sm leading-relaxed">
                Yes! All orders come beautifully packaged in our signature gift box 
                with a meaning card explaining the Morse code message.
              </p>
            </div>

            <div className="bg-[#F5F5F0] p-6 rounded-sm">
              <h3 className="font-serif text-lg text-[#354F52] mb-2">
                Do you ship internationally?
              </h3>
              <p className="text-[#52796F] text-sm leading-relaxed">
                Currently we ship within Cyprus only with free delivery via ACS Couriers. 
                International shipping is coming soon! Contact us to be notified.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
